/**
 * <ec-filters>
 *
 * Wraps a Shopify storefront filtering form. Three responsibilities:
 *
 * 1. AJAX-rerender the section via Shopify's Section Rendering API
 *    (no full page reload). Uses @theme/section-renderer.renderSection()
 *    which fetches `?section_id=<id>` and morph-diffs the existing
 *    `<div id="shopify-section-<id>">` wrapper.
 *
 * 2. Desktop (>=1024px): apply filters automatically when a facet
 *    changes (debounced).
 *
 * 3. Mobile (<1024px): turn the sidebar into an off-canvas drawer.
 *    The drawer's own facets apply only when the user taps
 *    "Voir N résultats" — the UI feels deliberate and avoids reload
 *    thrashing. Controls that live outside the drawer (the sort select,
 *    in the toolbar) have no commit button and apply immediately.
 *
 * Pagination (.ec-pagination__link) is also intercepted so navigating
 * between pages stays AJAX-driven.
 *
 * Fallback: if the section id is missing or the fetch fails, the form
 * is submitted normally (full reload). Keyboard navigation works either
 * way — the underlying <input> elements remain.
 */

import { trapFocus } from '@ec/focus-trap';
import { lockScroll } from '@ec/scroll-lock';
import { sectionRenderer } from '@theme/section-renderer';

const MOBILE_QUERY = '(max-width: 1023px)';
const AUTO_APPLY_DELAY = 250;
const TRANSITION_FALLBACK_MS = 350;
const COUNT_PLACEHOLDER = '__COUNT__';

class EcFilters extends HTMLElement {
  #form = null;
  #panel = null;
  #overlay = null;
  #mq = null;
  #releaseTrap = null;
  #releaseLock = null;
  #autoApplyTimer = null;
  #previewTimer = null;
  #previewToken = 0;
  #previousFocus = null;
  #isApplying = false;
  #onChange = null;
  #onSubmit = null;
  #onClick = null;
  #onKeydown = null;
  #onDocClick = null;
  #onMqChange = null;
  #onPopstate = null;

  connectedCallback() {
    this.#form = this.querySelector('form');
    this.#panel = this.querySelector('.ec-filters__sidebar');
    this.#overlay = this.querySelector('.ec-filters__overlay');
    this.#mq = window.matchMedia(MOBILE_QUERY);

    this.#onChange = (event) => {
      if (!this.#form) return;
      // Skip intermediate range events; ec-price-range dispatches a single
      // change at release time.
      const target = event.target;
      if (target instanceof HTMLElement && target.dataset.ecPriceRangeSilent === 'true') {
        return;
      }
      // Deferring only makes sense for controls the drawer can commit: the
      // "Appliquer" button belongs to the panel. The sort select sits in the
      // toolbar, outside the panel, with nothing to validate it — so it
      // applies straight away, at every width.
      if (this.#isMobile() && this.#isInDrawer(target)) {
        // Mobile drawer: don't auto-apply; just refresh the CTA count so
        // the user previews the projected result set before committing.
        this.#queuePreviewCount();
        return;
      }
      this.#queueAutoApply();
    };

    this.#onSubmit = (event) => {
      if (!this.sectionId) return; // no section id → let the form submit normally
      event.preventDefault();
      this.#applyFilters();
      if (this.#isMobile()) this.close();
    };

    this.#onClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      // Drawer close
      if (target.closest('[data-ec-filters-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      // Pagination links → keep AJAX
      const paginationLink = target.closest('.ec-pagination__link');
      if (paginationLink instanceof HTMLAnchorElement && this.sectionId) {
        event.preventDefault();
        const url = new URL(paginationLink.href, window.location.href);
        this.#applyFilters({ url });
        return;
      }

      // Active filter chips and reset links → keep AJAX
      const ajaxLink = target.closest('a[data-ec-ajax-nav]');
      if (ajaxLink instanceof HTMLAnchorElement && this.sectionId) {
        event.preventDefault();
        const url = new URL(ajaxLink.href, window.location.href);
        this.#applyFilters({ url });
      }
    };

    this.#onKeydown = (event) => {
      if (event.key === 'Escape' && this.dataset.open === 'true') {
        event.preventDefault();
        this.close();
      }
    };

    this.#onDocClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const trigger = target.closest('[data-ec-filters-open]');
      if (!trigger) return;
      const targetId = trigger.getAttribute('data-ec-filters-open');
      if (targetId && targetId !== this.id) return;
      event.preventDefault();
      this.open({ previousFocus: trigger instanceof HTMLElement ? trigger : null });
    };

    this.#onMqChange = () => {
      if (!this.#isMobile() && this.dataset.open === 'true') {
        this.#closeImmediate();
      }
    };

    this.#onPopstate = () => {
      if (!this.sectionId) return;
      const url = new URL(window.location.href);
      this.#runRender(url, { skipPushState: true });
    };

    this.#form?.addEventListener('change', this.#onChange);
    this.#form?.addEventListener('submit', this.#onSubmit);
    this.addEventListener('click', this.#onClick);
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onDocClick);
    this.#mq.addEventListener('change', this.#onMqChange);
    window.addEventListener('popstate', this.#onPopstate);
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('change', this.#onChange);
    this.#form?.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('click', this.#onDocClick);
    this.#mq?.removeEventListener('change', this.#onMqChange);
    window.removeEventListener('popstate', this.#onPopstate);
    this.#cleanup();
    if (this.#autoApplyTimer) {
      clearTimeout(this.#autoApplyTimer);
      this.#autoApplyTimer = null;
    }
    if (this.#previewTimer) {
      clearTimeout(this.#previewTimer);
      this.#previewTimer = null;
    }
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  /** The wrapper that gets the loading state (for opacity dim). */
  get loadingHost() {
    return this.closest('.ec-collection, .ec-search-page') ?? this;
  }

  open(opts = {}) {
    if (!this.#isMobile()) return;
    if (this.dataset.open === 'true') return;
    this.#previousFocus =
      opts.previousFocus instanceof HTMLElement
        ? opts.previousFocus
        : document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;

    this.dataset.open = 'true';
    this.#releaseLock = lockScroll();
    this.#releaseTrap = trapFocus(this.#panel || this);

    const closeBtn = this.querySelector('[data-ec-filters-close]');
    if (closeBtn instanceof HTMLElement) {
      closeBtn.focus({ preventScroll: true });
    }

    this.dispatchEvent(new CustomEvent('ec:filters:open', { bubbles: true }));
  }

  close() {
    if (this.dataset.open !== 'true') return;
    let resolved = false;
    const finish = () => {
      if (resolved) return;
      resolved = true;
      this.#closeImmediate();
    };

    this.dataset.open = 'closing';

    if (this.#panel) {
      const onTransitionEnd = (event) => {
        if (event.target !== this.#panel) return;
        this.#panel.removeEventListener('transitionend', onTransitionEnd);
        finish();
      };
      this.#panel.addEventListener('transitionend', onTransitionEnd);
      setTimeout(finish, TRANSITION_FALLBACK_MS);
    } else {
      finish();
    }
  }

  #closeImmediate() {
    delete this.dataset.open;
    this.#cleanup();
    if (this.#previousFocus && document.body.contains(this.#previousFocus)) {
      this.#previousFocus.focus({ preventScroll: true });
    }
    this.#previousFocus = null;
    this.dispatchEvent(new CustomEvent('ec:filters:close', { bubbles: true }));
  }

  #cleanup() {
    if (typeof this.#releaseTrap === 'function') {
      this.#releaseTrap();
      this.#releaseTrap = null;
    }
    if (typeof this.#releaseLock === 'function') {
      this.#releaseLock();
      this.#releaseLock = null;
    }
  }

  #isMobile() {
    return Boolean(this.#mq && this.#mq.matches);
  }

  /**
   * True when the control belongs to the off-canvas panel, i.e. the
   * "Appliquer" button can commit it.
   *
   * Resolved via closest() rather than against the cached #panel: a morph
   * may swap the sidebar node, and a stale reference would silently turn
   * every facet into an auto-apply one.
   */
  #isInDrawer(target) {
    if (!(target instanceof Element)) return false;
    return Boolean(target.closest('.ec-filters__sidebar'));
  }

  #queueAutoApply() {
    if (this.#autoApplyTimer) clearTimeout(this.#autoApplyTimer);
    this.#autoApplyTimer = window.setTimeout(() => {
      this.#autoApplyTimer = null;
      this.#applyFilters();
    }, AUTO_APPLY_DELAY);
  }

  #queuePreviewCount() {
    if (this.#previewTimer) clearTimeout(this.#previewTimer);
    this.#previewTimer = window.setTimeout(() => {
      this.#previewTimer = null;
      this.#fetchPreviewCount();
    }, AUTO_APPLY_DELAY);
  }

  /**
   * Mobile-only: fetch the section HTML for the pending form state and
   * update the apply button label/disabled with the projected product count.
   * Does not morph the DOM — the user still has to commit via submit.
   *
   * Reuses sectionRenderer's cache (same URL as the eventual commit), so
   * the apply submit gets a cache hit and no extra network round-trip.
   * A monotonic token discards stale responses when the user changes filters
   * faster than the network can keep up.
   */
  async #fetchPreviewCount() {
    if (!this.sectionId || !this.#form) return;
    const url = this.#buildFormURL();
    if (!url) return;
    const token = ++this.#previewToken;
    let html;
    try {
      html = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
    } catch (err) {
      // Preview is best-effort; on failure keep the existing label.
      return;
    }
    if (token !== this.#previewToken) return; // a newer preview superseded us
    const count = extractCount(html);
    if (count == null) return;
    this.#updateApplyButton(count);
  }

  #updateApplyButton(count) {
    const btn = this.querySelector('.ec-filters__apply');
    if (!(btn instanceof HTMLElement)) return;
    const label = pickApplyLabel(btn, count);
    if (label != null) btn.textContent = label;
    btn.toggleAttribute('disabled', count === 0);
    btn.dataset.ecProductsCount = String(count);
  }

  /**
   * Build a URL from the current form state and AJAX-rerender the section.
   * If `opts.url` is provided (pagination, chip), use it directly instead.
   */
  async #applyFilters(opts = {}) {
    if (!this.sectionId) {
      this.#form?.submit();
      return;
    }
    // Drop any in-flight preview so a stale response can't overwrite the
    // fresh button after morph.
    this.#previewToken++;
    if (this.#previewTimer) {
      clearTimeout(this.#previewTimer);
      this.#previewTimer = null;
    }
    const url = opts.url instanceof URL ? opts.url : this.#buildFormURL();
    if (!url) return;
    await this.#runRender(url);
  }

  #buildFormURL() {
    if (!this.#form) return null;
    const action = this.#form.getAttribute('action') || window.location.pathname;
    const url = new URL(action, window.location.href);
    // Replace search entirely with the current form state to drop empty filters.
    url.search = '';
    const formData = new FormData(this.#form);
    for (const [key, value] of formData) {
      if (typeof value !== 'string') continue;
      const trimmed = value.trim();
      if (trimmed === '') continue;
      url.searchParams.append(key, trimmed);
    }
    return url;
  }

  /**
   * Put the viewport back on the head of the results after a morph.
   *
   * The morph leaves the window exactly where it was, so paginating from the
   * foot of a page lands the visitor at the foot of the next one. We only
   * move when that head has already scrolled off above the fold: ticking a
   * facet while reading the first rows must not yank the page around.
   *
   * @returns {boolean} true when a scroll was issued.
   */
  #scrollToResults() {
    const results = this.querySelector('.ec-collection__main');
    if (!(results instanceof HTMLElement)) return false;
    // A sticky header reappears the moment we scroll up, so leave its height
    // clear or it lands on top of the count and the first product row.
    const header = document.querySelector('ec-header[sticky]');
    const offset = header instanceof HTMLElement ? header.offsetHeight : 0;
    const delta = results.getBoundingClientRect().top - offset;
    if (delta >= 0) return false; // head of the results is already on screen
    window.scrollTo({
      top: Math.max(0, window.scrollY + delta),
      // 'auto' defers to html { scroll-behavior: smooth }, so opting out of
      // motion has to name 'instant' explicitly.
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'instant'
        : 'smooth',
    });
    return true;
  }

  async #runRender(url, opts = {}) {
    if (this.#isApplying) return;
    this.#isApplying = true;
    const host = this.loadingHost;
    if (host instanceof HTMLElement) host.dataset.loading = 'true';

    const focusInfo = captureFocus();

    if (!opts.skipPushState) {
      try {
        history.pushState({ ecCollection: true }, '', url.toString());
      } catch (e) {
        // pushState can throw in some sandbox contexts; ignore and continue.
      }
    }

    try {
      await sectionRenderer.renderSection(this.sectionId, { url });
      // After morph: ask each <ec-price-range> to resync its state from
      // the freshly-morphed data-* attributes. attributeChangedCallback
      // is set up but morph helpers can skip a notification in edge
      // cases, so we invoke the public refresh method explicitly.
      this.querySelectorAll('ec-price-range').forEach((node) => {
        if (typeof node.refreshState === 'function') node.refreshState();
      });
      const settle = () => {
        // popstate is left alone: the browser restores the history entry's
        // own offset, and fighting it makes the Back button feel broken.
        if (opts.skipPushState || !this.#scrollToResults()) {
          // The morph normally preserves the offset, but a shorter result
          // set can leave it past the end of the document.
          const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
          if (window.scrollY > maxY) window.scrollTo({ top: maxY, behavior: 'instant' });
        }
      };
      if (this.dataset.open === 'closing') {
        // The mobile drawer still holds the scroll lock, and releasing it
        // replays the pre-lock offset — scrolling now would just be undone.
        this.addEventListener('ec:filters:close', settle, { once: true });
      } else {
        settle();
      }
    } catch (err) {
      // Network or section error → fall back to full navigation.
      window.location.href = url.toString();
      return;
    } finally {
      this.#isApplying = false;
      if (host instanceof HTMLElement) delete host.dataset.loading;
    }

    restoreFocus(focusInfo);
  }
}

/** Capture enough info to refocus the active element after a morph. */
function captureFocus() {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement)) return null;
  const selector = stableSelector(active);
  if (!selector) return null;
  const info = { selector };
  if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) {
    info.selectionStart = active.selectionStart;
    info.selectionEnd = active.selectionEnd;
  }
  return info;
}

function stableSelector(el) {
  if (el.id) return `#${CSS.escape(el.id)}`;
  // For form inputs prefer name + value to disambiguate radios/checkboxes.
  if ((el instanceof HTMLInputElement || el instanceof HTMLSelectElement) && el.name) {
    if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) {
      return `[name="${CSS.escape(el.name)}"][value="${CSS.escape(el.value)}"]`;
    }
    return `[name="${CSS.escape(el.name)}"]`;
  }
  return null;
}

function restoreFocus(info) {
  if (!info?.selector) return;
  const el = document.querySelector(info.selector);
  if (!(el instanceof HTMLElement)) return;
  el.focus({ preventScroll: true });
  if (
    info.selectionStart != null &&
    (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)
  ) {
    try {
      el.setSelectionRange(info.selectionStart, info.selectionEnd ?? info.selectionStart);
    } catch (e) {
      // Some input types (range, checkbox…) reject setSelectionRange.
    }
  }
}

/**
 * Pulls the projected product count out of a freshly fetched section HTML
 * string. We read it from the apply button's data-ec-products-count attr
 * (rendered server-side) rather than parsing the visible label — robust
 * across locales and pluralization rules.
 */
function extractCount(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const btn = doc.querySelector('.ec-filters__apply[data-ec-products-count]');
  if (!btn) return null;
  const raw = btn.getAttribute('data-ec-products-count');
  if (raw == null) return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
}

function pickApplyLabel(btn, count) {
  if (count === 0) return btn.dataset.labelZero ?? btn.textContent;
  if (count === 1) return btn.dataset.labelOne ?? btn.textContent;
  const template = btn.dataset.labelOther;
  if (!template) return btn.textContent;
  return template.replace(COUNT_PLACEHOLDER, String(count));
}

if (!customElements.get('ec-filters')) {
  customElements.define('ec-filters', EcFilters);
}
